import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api, query } from './client'
import type {
  ApiKey,
  AuditPage,
  CheckoutConfig,
  Comparison,
  CreatedApiKey,
  Environment,
  FindingsResponse,
  FlameNode,
  FunctionRow,
  Instance,
  Invitation,
  InvitationPreview,
  ListResponse,
  NotificationChannel,
  NotificationChannelList,
  PlatformCounts,
  PreviousBuild,
  ProfileListResponse,
  ProfileShare,
  ProfileSummary,
  ProfileType,
  Profile_,
  Project,
  ProjectRoleEntry,
  Relations,
  Role,
  Service,
  Session,
  SubscriptionState,
  SupportConversation,
  SupportDesk,
  SupportMessage,
  Team,
  TelegramLink,
  Trend,
  Usage,
  Version,
} from './types'

/**
 * Query keys are structured so that invalidating a prefix invalidates
 * everything below it: dropping `['project', id]` refreshes its services,
 * profiles and keys in one call.
 */
export const keys = {
  me: ['me'] as const,
  projects: ['projects'] as const,
  project: (id: string) => ['project', id] as const,
  apiKeys: (projectId: string) => ['project', projectId, 'api-keys'] as const,
  services: (projectId: string) => ['project', projectId, 'services'] as const,
  service: (projectId: string, serviceId: string) => ['project', projectId, 'service', serviceId] as const,
  environments: (projectId: string) => ['project', projectId, 'environments'] as const,
  serviceEnvironments: (projectId: string, serviceId: string) =>
    ['project', projectId, 'service', serviceId, 'environments'] as const,
  instances: (projectId: string, serviceId: string, environmentId?: string) =>
    ['project', projectId, 'service', serviceId, 'instances', environmentId ?? 'all'] as const,
  profiles: (projectId: string, filter: ProfileFilter) =>
    ['project', projectId, 'profiles', filter] as const,
  profile: (projectId: string, profileId: string) =>
    ['project', projectId, 'profile', profileId] as const,
  functions: (projectId: string, profileId: string, sort: string) =>
    ['project', projectId, 'profile', profileId, 'functions', sort] as const,
  flame: (projectId: string, profileId: string, reverseOf: string) =>
    ['project', projectId, 'profile', profileId, 'flame', reverseOf] as const,
  relations: (projectId: string, profileId: string, fn: string) =>
    ['project', projectId, 'profile', profileId, 'relations', fn] as const,
  findings: (projectId: string, profileId: string) =>
    ['project', projectId, 'profile', profileId, 'findings'] as const,
  comparison: (projectId: string, before: string, after: string) =>
    ['project', projectId, 'compare', before, after] as const,
  usage: ['usage'] as const,
  team: ['team'] as const,
  support: ['support'] as const,
  supportThread: (id: string) => ['support', id] as const,
  audit: (action: string, offset: number) => ['audit', action, offset] as const,
  projectRoles: (projectId: string) => ['project', projectId, 'roles'] as const,
  notifications: (projectId: string) => ['project', projectId, 'notifications'] as const,
  shares: (projectId: string, profileId: string) =>
    ['project', projectId, 'profile', profileId, 'shares'] as const,
  trend: (projectId: string, filter: object) => ['project', projectId, 'trend', filter] as const,
  telegramLink: (projectId: string, linkId: string) =>
    ['project', projectId, 'notifications', 'telegram-link', linkId] as const,
}

export interface ProfileFilter {
  service_id?: string
  environment_id?: string
  instance_id?: string
  profile_type?: ProfileType
  limit?: number
  offset?: number
}

export function useCurrentUser(enabled: boolean) {
  return useQuery({
    queryKey: keys.me,
    queryFn: ({ signal }) => api.get<Profile_>('/auth/me', signal),
    enabled,
    retry: false,
  })
}

export function useLogin() {
  return useMutation({
    mutationFn: (input: { email: string; password: string }) =>
      api.post<Session>('/auth/login', input),
  })
}

export function useRegister() {
  return useMutation({
    mutationFn: (input: { email: string; password: string; organization_name?: string }) =>
      api.post<Session>('/auth/register', input),
  })
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (input: { current_password: string; new_password: string }) =>
      api.post<Session>('/auth/password', input),
  })
}

export function useRequestPasswordReset() {
  return useMutation({
    mutationFn: (input: { email: string }) =>
      api.post<void>('/auth/password-reset/request', input),
  })
}

export function useConfirmPasswordReset() {
  return useMutation({
    mutationFn: (input: { email: string; code: string; new_password: string }) =>
      api.post<void>('/auth/password-reset/confirm', input),
  })
}

export function useProjects() {
  return useQuery({
    queryKey: keys.projects,
    queryFn: ({ signal }) => api.get<ListResponse<Project>>('/projects', signal),
  })
}

export function useProject(projectId: string | undefined) {
  return useQuery({
    queryKey: keys.project(projectId ?? ''),
    queryFn: ({ signal }) => api.get<Project>(`/projects/${projectId}`, signal),
    enabled: Boolean(projectId),
  })
}

export function useCreateProject() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { name: string; slug?: string }) => api.post<Project>('/projects', input),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.projects })
      void client.invalidateQueries({ queryKey: keys.usage })
    },
  })
}

export function useUpdateProject(projectId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { name?: string; slug?: string }) =>
      api.patch<Project>(`/projects/${projectId}`, input),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.projects })
      void client.invalidateQueries({ queryKey: keys.project(projectId) })
    },
  })
}

export function useDeleteProject() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (projectId: string) => api.delete<void>(`/projects/${projectId}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.projects })
      void client.invalidateQueries({ queryKey: keys.usage })
    },
  })
}

export function useApiKeys(projectId: string | undefined) {
  return useQuery({
    queryKey: keys.apiKeys(projectId ?? ''),
    queryFn: ({ signal }) => api.get<ListResponse<ApiKey>>(`/projects/${projectId}/api-keys`, signal),
    enabled: Boolean(projectId),
  })
}

export function useCreateApiKey(projectId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { name: string }) =>
      api.post<CreatedApiKey>(`/projects/${projectId}/api-keys`, input),
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.apiKeys(projectId) }),
  })
}

export function useRevokeApiKey(projectId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (keyId: string) => api.delete<void>(`/projects/${projectId}/api-keys/${keyId}`),
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.apiKeys(projectId) }),
  })
}

export function useServices(projectId: string | undefined) {
  return useQuery({
    queryKey: keys.services(projectId ?? ''),
    queryFn: ({ signal }) => api.get<ListResponse<Service>>(`/projects/${projectId}/services`, signal),
    enabled: Boolean(projectId),
  })
}

export function useService(projectId: string | undefined, serviceId: string | undefined) {
  return useQuery({
    queryKey: keys.service(projectId ?? '', serviceId ?? ''),
    queryFn: ({ signal }) => api.get<Service>(`/projects/${projectId}/services/${serviceId}`, signal),
    enabled: Boolean(projectId && serviceId),
  })
}

/**
 * useSetSilenceAlerts turns the "stopped reporting" alert on or off.
 *
 * A service that is meant to go quiet — a batch job, or one whose profiler
 * stops itself after a couple of hours — otherwise raises an alert for doing
 * exactly what it was told, and an alert channel that cries wolf gets muted.
 */
export function useSetSilenceAlerts(projectId: string, serviceId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (enabled: boolean) =>
      api.patch<Service>(`/projects/${projectId}/services/${serviceId}`, {
        silence_alerts: enabled,
      }),
    onSuccess: (service) => {
      client.setQueryData(keys.service(projectId, serviceId), service)
      void client.invalidateQueries({ queryKey: keys.services(projectId) })
    },
  })
}

export function useEnvironments(projectId: string | undefined) {
  return useQuery({
    queryKey: keys.environments(projectId ?? ''),
    queryFn: ({ signal }) =>
      api.get<ListResponse<Environment>>(`/projects/${projectId}/environments`, signal),
    enabled: Boolean(projectId),
  })
}

export function useServiceEnvironments(projectId: string | undefined, serviceId: string | undefined) {
  return useQuery({
    queryKey: keys.serviceEnvironments(projectId ?? '', serviceId ?? ''),
    queryFn: ({ signal }) =>
      api.get<ListResponse<Environment>>(
        `/projects/${projectId}/services/${serviceId}/environments`,
        signal,
      ),
    enabled: Boolean(projectId && serviceId),
  })
}

export function useInstances(
  projectId: string | undefined,
  serviceId: string | undefined,
  environmentId?: string,
) {
  return useQuery({
    queryKey: keys.instances(projectId ?? '', serviceId ?? '', environmentId),
    queryFn: ({ signal }) =>
      api.get<ListResponse<Instance>>(
        `/projects/${projectId}/services/${serviceId}/instances${query({ environment_id: environmentId })}`,
        signal,
      ),
    enabled: Boolean(projectId && serviceId),
  })
}

export function useProfiles(projectId: string | undefined, filter: ProfileFilter = {}) {
  return useQuery({
    queryKey: keys.profiles(projectId ?? '', filter),
    queryFn: ({ signal }) =>
      api.get<ProfileListResponse>(`/projects/${projectId}/profiles${query({ ...filter })}`, signal),
    enabled: Boolean(projectId),
  })
}

export function useProfile(projectId: string | undefined, profileId: string | undefined) {
  return useQuery({
    queryKey: keys.profile(projectId ?? '', profileId ?? ''),
    queryFn: ({ signal }) =>
      api.get<ProfileSummary>(`/projects/${projectId}/profiles/${profileId}`, signal),
    enabled: Boolean(projectId && profileId),
  })
}

export function useProfileFunctions(
  projectId: string | undefined,
  profileId: string | undefined,
  sort: string,
) {
  return useQuery({
    queryKey: keys.functions(projectId ?? '', profileId ?? '', sort),
    queryFn: ({ signal }) =>
      api.get<ListResponse<FunctionRow>>(
        `/projects/${projectId}/profiles/${profileId}/functions${query({ sort, limit: 200 })}`,
        signal,
      ),
    enabled: Boolean(projectId && profileId),
  })
}

export function useFlameGraph(
  projectId: string | undefined,
  profileId: string | undefined,
  reverseOf = '',
) {
  return useQuery({
    queryKey: keys.flame(projectId ?? '', profileId ?? '', reverseOf),
    queryFn: ({ signal }) =>
      api.get<FlameNode>(
        `/projects/${projectId}/profiles/${profileId}/flame${query({ reverse_of: reverseOf })}`,
        signal,
      ),
    enabled: Boolean(projectId && profileId),
  })
}

export function useRelations(
  projectId: string | undefined,
  profileId: string | undefined,
  functionName: string,
) {
  return useQuery({
    queryKey: keys.relations(projectId ?? '', profileId ?? '', functionName),
    queryFn: ({ signal }) =>
      api.get<Relations>(
        `/projects/${projectId}/profiles/${profileId}/relations${query({ function: functionName })}`,
        signal,
      ),
    enabled: Boolean(projectId && profileId && functionName),
  })
}

export function useFindings(projectId: string | undefined, profileId: string | undefined) {
  return useQuery({
    queryKey: keys.findings(projectId ?? '', profileId ?? ''),
    queryFn: ({ signal }) =>
      api.get<FindingsResponse>(`/projects/${projectId}/profiles/${profileId}/findings`, signal),
    enabled: Boolean(projectId && profileId),
  })
}

export function useComparison(
  projectId: string | undefined,
  before: string | undefined,
  after: string | undefined,
) {
  return useQuery({
    queryKey: keys.comparison(projectId ?? '', before ?? '', after ?? ''),
    queryFn: ({ signal }) =>
      api.get<Comparison>(`/projects/${projectId}/profiles/compare${query({ before, after })}`, signal),
    enabled: Boolean(projectId && before && after),
  })
}

export function useDeleteProfile(projectId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (profileId: string) => api.delete<void>(`/projects/${projectId}/profiles/${profileId}`),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['project', projectId, 'profiles'] }),
  })
}

export interface UploadInput {
  service: string
  environment: string
  profile_type: ProfileType
  file: File
}

export function useUploadProfile(projectId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (input: UploadInput) => {
      const form = new FormData()
      form.append(
        'metadata',
        JSON.stringify({
          service: input.service,
          environment: input.environment,
          profile_type: input.profile_type,
        }),
      )
      form.append('profile', input.file)
      return api.upload<{ profile: ProfileSummary }>(`/projects/${projectId}/uploads`, form)
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.project(projectId) })
      void client.invalidateQueries({ queryKey: keys.usage })
    },
  })
}

/**
 * The platform totals shown on the landing page.
 *
 * Read by visitors with no account, so it never carries a token and never
 * retries into a loading spinner: a marketing line that cannot be fetched is
 * simply not drawn.
 */
/**
 * The build this profile is measured against.
 *
 * The same question the regression detector asks, so the button offers exactly
 * the comparison an alert would have described.
 */
export function usePreviousBuild(projectId: string, profileId: string) {
  return useQuery({
    queryKey: ['previous-build', projectId, profileId],
    queryFn: ({ signal }) =>
      api.get<PreviousBuild>(`/projects/${projectId}/profiles/${profileId}/previous-build`, signal),
    enabled: Boolean(projectId && profileId),
  })
}

export function usePlatformCounts() {
  return useQuery({
    queryKey: ['platform-counts'],
    queryFn: ({ signal }) => api.get<PlatformCounts>('/stats', signal),
    staleTime: 5 * 60 * 1000,
    retry: false,
  })
}

export function useUsage(options?: {
  refetchInterval?: (query: { state: { data: Usage | undefined } }) => number | false
}) {
  return useQuery({
    queryKey: keys.usage,
    queryFn: ({ signal }) => api.get<Usage>('/billing/usage', signal),
    ...options,
  })
}

/**
 * The checkout configuration.
 *
 * Not fetched until somebody is about to buy: it is useless to a visitor who
 * is only reading the prices, and it answers 503 on a deployment where billing
 * is not configured, which would otherwise be a permanent error on the pricing
 * page of every development machine.
 */
export function useCheckoutConfig(enabled: boolean) {
  return useQuery({
    queryKey: ['billing', 'checkout'],
    queryFn: ({ signal }) => api.get<CheckoutConfig>('/billing/paddle/checkout', signal),
    enabled,
    retry: false,
  })
}

export function useSubscription(options?: {
  refetchInterval?: (query: { state: { data: SubscriptionState | undefined } }) => number | false
}) {
  return useQuery({
    queryKey: ['billing', 'subscription'],
    queryFn: ({ signal }) => api.get<SubscriptionState>('/billing/paddle/subscription', signal),
    ...options,
  })
}

export function useNotificationChannels(projectId: string | undefined) {
  return useQuery({
    queryKey: keys.notifications(projectId ?? ''),
    queryFn: ({ signal }) =>
      api.get<NotificationChannelList>(`/projects/${projectId}/notifications`, signal),
    enabled: Boolean(projectId),
  })
}

export function useConnectSlack(projectId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { webhook_url: string; name?: string }) =>
      api.post<NotificationChannel>(`/projects/${projectId}/notifications/slack`, input),
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.notifications(projectId) }),
  })
}

export function useUpdateNotificationChannel(projectId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...patch }: { id: string } & Partial<NotificationChannel>) =>
      api.patch<NotificationChannel>(`/projects/${projectId}/notifications/${id}`, patch),
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.notifications(projectId) }),
  })
}

export function useDisconnectNotificationChannel(projectId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (channelId: string) =>
      api.delete<void>(`/projects/${projectId}/notifications/${channelId}`),
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.notifications(projectId) }),
  })
}

export function useSendTestNotification(projectId: string) {
  return useMutation({
    mutationFn: (channelId: string) =>
      api.post<{ status: string }>(`/projects/${projectId}/notifications/${channelId}/test`, {}),
  })
}

export function useStartTelegramLink(projectId: string) {
  return useMutation({
    mutationFn: (input: { chat?: string }) =>
      api.post<TelegramLink>(`/projects/${projectId}/notifications/telegram/link`, input),
  })
}

/**
 * Polls a pairing while the bot has not answered yet.
 *
 * The interval stops once the chat has claimed the code: from that point the
 * user is typing, and there is nothing left for the server to tell us.
 */
export function useTelegramLink(projectId: string, linkId: string | undefined) {
  return useQuery({
    queryKey: keys.telegramLink(projectId, linkId ?? ''),
    queryFn: ({ signal }) =>
      api.get<TelegramLink>(`/projects/${projectId}/notifications/telegram/link/${linkId}`, signal),
    enabled: Boolean(linkId),
    refetchInterval: (query) => (query.state.data?.stage === 'awaiting_chat' ? 2000 : false),
  })
}

export function useConfirmTelegramLink(projectId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ linkId, code, name }: { linkId: string; code: string; name?: string }) =>
      api.post<NotificationChannel>(
        `/projects/${projectId}/notifications/telegram/link/${linkId}/confirm`,
        { code, name },
      ),
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.notifications(projectId) }),
  })
}

export interface TrendFilter {
  service_id: string
  profile_type: ProfileType
  environment_id?: string
  function?: string
  /** How far back to look. Minutes, so a window can be shorter than a day. */
  minutes?: number
}

/**
 * useTrend asks how a service's profiles moved over time.
 *
 * The question a single profile cannot answer: whether the number in front of
 * you is normal for this service or the top of a climb.
 */
/**
 * useVersions lists the builds a service has run.
 *
 * The list a comparison should start from: a release is not one capture, and
 * picking two profiles out of a list asks about captures rather than builds.
 */
export function useVersions(
  projectId: string | undefined,
  filter: { service_id: string; profile_type: ProfileType; environment_id?: string } | undefined,
) {
  return useQuery({
    queryKey: ['project', projectId ?? '', 'versions', filter ?? {}] as const,
    queryFn: ({ signal }) =>
      api.get<ListResponse<Version>>(
        `/projects/${projectId}/profiles/versions${query({ ...filter })}`,
        signal,
      ),
    enabled: Boolean(projectId && filter?.service_id && filter?.profile_type),
  })
}

export function useTrend(projectId: string | undefined, filter: TrendFilter | undefined) {
  return useQuery({
    queryKey: keys.trend(projectId ?? '', filter ?? ({} as TrendFilter)),
    queryFn: ({ signal }) =>
      api.get<Trend>(`/projects/${projectId}/profiles/trend${query({ ...filter })}`, signal),
    enabled: Boolean(projectId && filter?.service_id && filter?.profile_type),
  })
}

export function useProfileShares(projectId: string | undefined, profileId: string | undefined) {
  return useQuery({
    queryKey: keys.shares(projectId ?? '', profileId ?? ''),
    queryFn: ({ signal }) =>
      api.get<ListResponse<ProfileShare>>(
        `/projects/${projectId}/profiles/${profileId}/shares`,
        signal,
      ),
    enabled: Boolean(projectId && profileId),
  })
}

export function useCreateProfileShare(projectId: string, profileId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { label?: string; days?: number }) =>
      api.post<ProfileShare>(`/projects/${projectId}/profiles/${profileId}/shares`, input),
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.shares(projectId, profileId) }),
  })
}

export function useRevokeProfileShare(projectId: string, profileId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (shareId: string) =>
      api.delete<void>(`/projects/${projectId}/profiles/${profileId}/shares/${shareId}`),
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.shares(projectId, profileId) }),
  })
}

/**
 * Membership.
 *
 * The team query is what the members page reads, and it carries the roles the
 * plan allows alongside the people — so the form offers exactly the roles that
 * can be granted rather than four that may each be refused.
 */
export function useTeam() {
  return useQuery({
    queryKey: keys.team,
    queryFn: ({ signal }) => api.get<Team>('/members', signal),
  })
}

export function useInvite() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { email: string; role: Role }) =>
      api.post<Invitation>('/members/invitations', input),
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.team }),
  })
}

export function useRevokeInvitation() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/members/invitations/${id}`),
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.team }),
  })
}

export function useChangeMemberRole() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: Role }) =>
      api.patch<void>(`/members/${userId}`, { role }),
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.team }),
  })
}

export function useRemoveMember() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (userId: string) => api.delete<void>(`/members/${userId}`),
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.team }),
  })
}

/** The invitation behind a link, read before anybody types a password. */
export function useInvitationPreview(token: string | undefined) {
  return useQuery({
    queryKey: ['invitation', token ?? ''],
    queryFn: ({ signal }) => api.get<InvitationPreview>(`/invitations/${token}`, signal),
    enabled: Boolean(token),
    retry: false,
  })
}

/** Per-project roles, which only the top plans can set. */
export function useProjectRoles(projectId: string | undefined) {
  return useQuery({
    queryKey: keys.projectRoles(projectId ?? ''),
    queryFn: ({ signal }) =>
      api.get<ListResponse<ProjectRoleEntry>>(`/projects/${projectId}/members`, signal),
    enabled: Boolean(projectId),
  })
}

export function useSetProjectRole(projectId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: Role }) =>
      api.put<void>(`/projects/${projectId}/members/${userId}`, { role }),
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.projectRoles(projectId) }),
  })
}

export function useClearProjectRole(projectId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (userId: string) => api.delete<void>(`/projects/${projectId}/members/${userId}`),
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.projectRoles(projectId) }),
  })
}

/** Members is the one thing not everybody may change. */
export function canManageMembers(role: Role | undefined): boolean {
  return role === 'owner' || role === 'admin'
}

/**
 * The audit log.
 *
 * Recorded for everybody and readable on the plans that include it, so a
 * refusal here is a quota error rather than an empty page — the events exist,
 * this organization is not paying to read them.
 */
export function useAuditLog(action: string, offset: number, limit = 50) {
  return useQuery({
    queryKey: keys.audit(action, offset),
    queryFn: ({ signal }) => {
      const params = new URLSearchParams({ limit: String(limit), offset: String(offset) })
      if (action) params.set('action', action)
      return api.get<AuditPage>(`/audit?${params.toString()}`, signal)
    },
    retry: false,
  })
}

/** The support desk. */
export function useSupportDesk(enabled: boolean) {
  return useQuery({
    queryKey: keys.support,
    queryFn: ({ signal }) => api.get<SupportDesk>('/support', signal),
    enabled,
  })
}

export function useSupportThread(id: string | undefined) {
  return useQuery({
    queryKey: keys.supportThread(id ?? ''),
    queryFn: ({ signal }) => api.get<SupportConversation>(`/support/${id}`, signal),
    enabled: Boolean(id),
    // An answer arrives from somebody typing on a phone, so an open thread
    // checks for one rather than waiting to be reloaded.
    refetchInterval: 30_000,
  })
}

export function useAskSupport() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { subject?: string; body: string }) =>
      api.post<SupportConversation>('/support', input),
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.support }),
  })
}

export function useReplyToSupport(conversationId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (body: string) =>
      api.post<SupportMessage>(`/support/${conversationId}/messages`, { body }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.supportThread(conversationId) })
      void client.invalidateQueries({ queryKey: keys.support })
    },
  })
}
